import { FC, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import { CustomRouteProps } from '@/types/router'
import {
  DefaultAvatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
} from '@purepm/portals-ui'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { PortalLaunchDarklyFlags } from '@purepm/purepm-lovs'
import LogoWithText from '../assets/images/logo-with-text.png'
import cn from '../lib/cn'
import routes from '../pages/routes'
import { btnClass, ButtonType } from './@legacy/Button'
import ErrorBoundary from './ErrorBoundary'
import HamburgerMenu from './HamburgerMenu'
import Nav from './Nav'
import LinkButton from './ui/LinkButton'
import { ROUTE_FEATURE_FLAGS } from '@/constants/featureFlagRoutes'
import { getDisplayName, getInitials } from '@purepm/fe-common'
import { LogOut } from 'lucide-react'

interface NavigationProps {
  children?: JSX.Element
  isInSideNav?: boolean
  hasSideNav?: boolean
}

const UserMenu = () => {
  const { user, logout } = useAuth0()
  const navigate = useNavigate()

  if (!user) return null

  const displayName = getDisplayName(user)
  const initials = getInitials(user)

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div data-testid="user-menu-trigger">
          <DefaultAvatar text={initials ?? ''} data-testid="default-avatar" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="p-2">
        <DropdownMenuItem
          className="cursor-pointer mb-1"
          data-testid="user-menu-item"
          onClick={() => navigate('/account/profile')}
        >
          <div className="flex items-center gap-3">
            <DefaultAvatar
              text={initials ?? ''}
              data-testid="dropdown-item-default-avatar"
            />
            <p className="text-sm font-semibold">{displayName}</p>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          data-testid="user-menu-item"
          onClick={handleLogout}
        >
          <div className="flex items-center gap-3">
            <LogOut className="h-4 w-4" />
            Logout
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem className="text-xs font-semibold" disabled>
          Version {import.meta.env.VITE_APP_VERSION}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const Navigation: FC<NavigationProps> = ({ children, hasSideNav = true }) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const flags = useFlags()

  const isContactUsPageEnabled =
    flags[PortalLaunchDarklyFlags.PORTAL_RENTER_MENU_CONTACT_US_ENABLED]
  const contactUsHref = 'https://purehomeriver.com/contact/'

  const toggleMenu = () => setMenuOpen(s => !s)
  const closeMenu = () => {
    if (menuOpen) {
      setMenuOpen(false)
    }
  }

  const renderTopNav = () => (
    <header
      className={cn(
        'nav-header h-[68px]',
        'bg-backgroundBlue border-b border-gray-300 z-10',
        'fixed w-full flex items-center justify-between',
        'pr-6 lg:px-6 z-50',
      )}
    >
      {hasSideNav ? (
        <>
          <HamburgerMenu onClick={toggleMenu} />
          <div
            className={cn(
              'h-20',
              'overflow-hidden flex-grow flex-shrink flex items-center w-0 opacity-0 whitespace-nowrap',
              'transition-all duration-300 lg:opacity-100 lg:w-full',
            )}
          >
            <img
              className="inline-block object-cover h-[44px]"
              src={LogoWithText}
              alt="Logo"
            />
          </div>
        </>
      ) : (
        <button
          onClick={() => navigate('/payments')}
          className={cn(
            'p-4 w-full',
            'overflow-hidden flex-grow flex-shrink flex items-center whitespace-nowrap',
            'lg:w-full',
          )}
        >
          <img
            className="cursor-pointer inline-block object-cover w-36"
            src={LogoWithText}
            alt="Logo"
          />
        </button>
      )}
      <div>
        <UserMenu />
      </div>
    </header>
  )

  const renderLinks = () =>
    routes.map(
      ({ path = '', name, isInSideNav, iconType = '' }: CustomRouteProps) => {
        const cleanPath = path.replace(/\/\*$/, '')
        const isActive = location.pathname.includes(cleanPath)
        const iconKey = isActive ? `${iconType}-selected` : iconType
        const routeFlag =
          ROUTE_FEATURE_FLAGS[path as keyof typeof ROUTE_FEATURE_FLAGS]

        // Check if the route has a feature flag and if it's enabled
        const isFlagEnabled = routeFlag === undefined ? true : flags[routeFlag]
        return (
          isInSideNav !== false &&
          isFlagEnabled && (
            <NavLink
              key={name}
              onClick={closeMenu}
              className={cn(
                'nav-link flex items-center gap-4 pl-6 py-6 font-semibold text-xs rounded',
                'hover:text-gray-600',
                {
                  'bg-gray-150 text-gray-900': isActive,
                  'text-gray-600': !isActive,
                },
              )}
              to={cleanPath}
              end={cleanPath === '/'}
            >
              <Icon type={iconKey} />
              <div
                className={cn(
                  'text truncate',
                  'transition-all duration-300',
                  'lg:block lg:w-full lg:opacity-100 font-semibold text-xs',
                  {
                    [menuOpen ? 'block' : 'hidden']: true,
                    [menuOpen ? 'opacity-100' : 'opacity-0']: true,
                    [menuOpen ? 'w-full' : 'w-0']: true,
                  },
                )}
              >
                {name?.toUpperCase()}
              </div>
            </NavLink>
          )
        )
      },
    )

  const renderContactUs = () => (
    <div className="bg-[#EFF6FF] border border-[#C3DEFE] rounded-lg p-3 mx-4 mt-4">
      <p className="font-medium leading-none">Need Help?</p>
      <p className="text-sm mt-2">Contact our property management office</p>
      {isContactUsPageEnabled ? (
        <Link
          to="/contact"
          onClick={closeMenu}
          className={cn(
            'btn rounded-lg inline-block text-center mt-4 w-full',
            'px-4 py-2.5 text-sm leading-4',
            btnClass[ButtonType.primary],
          )}
        >
          Contact Us
        </Link>
      ) : (
        <LinkButton
          target="_blank"
          href={contactUsHref}
          buttonType={ButtonType.primary}
          className="mt-4 w-full"
          size="sm"
        >
          Contact Us
        </LinkButton>
      )}
    </div>
  )

  const renderhasSideNav = () => {
    if (!hasSideNav) {
      return null
    }

    return (
      <Nav
        onClickClose={toggleMenu}
        isOpen={menuOpen}
        className="justify-between"
      >
        <div>{renderLinks()}</div>
        {renderContactUs()}
      </Nav>
    )
  }

  return (
    <>
      {renderTopNav()}
      {renderhasSideNav()}

      <main
        className={cn(
          'w-full flex-grow flex-shrink',
          'absolute top-[68px] left-0 right-0 bottom-0',
          { 'lg:pl-72 z-0': hasSideNav },
        )}
      >
        <article className="w-full h-full flex flex-col overflow-hidden">
          <div className="h-full overflow-y-auto">
            <Sentry.ErrorBoundary
              fallback={<ErrorBoundary />}
              beforeCapture={(scope: {
                setTag: (arg0: string, arg1: string) => void
              }) => {
                scope.setTag('pageload', 'error')
              }}
            >
              {children}
            </Sentry.ErrorBoundary>
          </div>
        </article>
      </main>
    </>
  )
}

export default Navigation
